import { useEffect, useMemo, useRef, useState } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { HeroFeedItem } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { isMotionReduced } from '#src/app/shared/utils/accessibility';

import { useScrollAnimation } from './scrollHelpers';

const ANIMATE_DELAY = 2000;
const ANIMATE_SPEED = 2000;
const PILL_DISPLAYED_COUNT_MAX = 4;

type HeroFeedResult = {
	// Mobile mode returns
	scrollerRef: React.RefObject<HTMLDivElement | null> | null;
	reversedItems: readonly HeroFeedItem[];
	// Desktop mode returns
	data: {
		displayedItems: readonly HeroFeedItem[];
		animateSpeed: number;
		animateDelay: number;
		a11y: {
			label: string;
			buttonLabel: string;
			isFeedAccessible: boolean;
		};
	};
	actions: {
		toggleFeedAccessibility: () => void;
	};
};

export function useHeroFeed(
	items: readonly HeroFeedItem[],
	intervalSeconds: number,
	mode: 'mobile' | 'desktop' = 'mobile',
): HeroFeedResult {
	const { translate } = useI18n();
	const isMobile = mode === 'mobile';
	const reversedItems = useMemo(() => [...items].reverse(), [items]);
	const [isFeedAccessible, setIsFeedAccessible] = useState(false);

	// Mobile vertical scroll logic
	const [shouldAnimate, setShouldAnimate] = useState(() => !isMotionReduced());
	const scrollerRef = useRef<HTMLDivElement>(null);
	useScrollAnimation(scrollerRef, intervalSeconds, isMobile && shouldAnimate, setShouldAnimate);

	// Desktop horizontal scroll logic
	const [displayItems, setDisplayItems] = useState(reversedItems);

	useEffect(() => {
		if (isMobile || displayItems.length <= 1) return undefined;

		const timeout = window.setTimeout(() => {
			setDisplayItems(displayItems.slice(1));
		}, ANIMATE_DELAY + ANIMATE_SPEED);

		return () => {
			window.clearTimeout(timeout);
		};
	}, [isMobile, displayItems]);

	const itemsTruncated = useMemo(
		() => (isMobile ? [] : displayItems.slice(0, PILL_DISPLAYED_COUNT_MAX)),
		[isMobile, displayItems],
	);

	return {
		scrollerRef: isMobile ? scrollerRef : null,
		reversedItems,
		data: {
			displayedItems: itemsTruncated,
			animateSpeed: ANIMATE_SPEED,
			animateDelay: ANIMATE_DELAY,
			a11y: {
				label: translate('corgi.psf.promote_or_share.supporters.summary'),
				buttonLabel: translate(`corgi.psf.promote_or_share.supporters.${isFeedAccessible ? 'hide' : 'view'}_all`),
				isFeedAccessible,
			},
		},
		actions: {
			toggleFeedAccessibility: () => setIsFeedAccessible((wasAccessible) => !wasAccessible),
		},
	};
}
